import React from 'react';
import PropTypes from 'prop-types';
import { Form, Col, Button, Spinner, Badge } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import IconAlert from 'components/common/IconAlert';

/**
 * Componente para el panel lateral con detalles y controles de la orden
 */
const PanelDetalleOrden = ({
    totales,
    datosFacturacion,
    formasPago,
    fechaLimitePago,
    confirmandoOrden,
    onCambiarFormaPago,
    onCambiarFechaVenta,
    onCambiarFechaVencimiento,
    onConfirmar,
    bIsNuevaOrden = true,
    onCancelar
}) => {
    return (
        <div>
            {/* Mini resumen facturación */}
            {(datosFacturacion?.vRfc || datosFacturacion?.vRazonSocial) && (
                <div className="mb-3 pb-3 border-bottom">
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        {totales.bIsDistribucion !== null && totales.bIsDistribucion !== undefined && (
                            <Badge bg={totales.bIsDistribucion ? 'info' : 'secondary'} className="fs--2">
                                {totales.bIsDistribucion ? 'Distribuidor' : 'Cliente directo'}
                            </Badge>
                        )}
                        {datosFacturacion?.vRfc && (
                            <span className="fs--2 text-muted">{datosFacturacion.vRfc}</span>
                        )}
                    </div>
                    {datosFacturacion?.vRazonSocial && (
                        <div className="fs--2 fw-semibold text-900 text-truncate">
                            {datosFacturacion.vRazonSocial}
                        </div>
                    )}
                </div>
            )}

            {/* Fecha de orden */}
            <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                <span className="text-muted fs--1">Fecha de Orden</span>
                <Col xs="auto" className="p-0">
                    <DatePicker
                        selected={new Date(totales.dFechaVenta)}
                        onChange={onCambiarFechaVenta}
                        maxDate={new Date()}
                        dateFormat="dd MMM yyyy"
                        className="form-control form-control-sm text-end border-0 fw-semibold bg-transparent"
                        style={{ width: '130px', cursor: 'pointer' }}
                    />
                </Col>
            </div>

            {/* Vencimiento */}
            {fechaLimitePago && (
                <>
                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                        <span className="text-muted fs--1">Vencimiento</span>
                        <Form.Control
                            onChange={(e) => onCambiarFechaVencimiento(e.target.value)}
                            type="date"
                            size="sm"
                            className="text-end border-0 fw-semibold bg-transparent p-0"
                            style={{ width: '130px' }}
                        />
                    </div>
                    <IconAlert className="mb-0 mt-2" variant="warning">
                        <p className="mb-0 fs--2">Fecha l&iacute;mite de pago requerida</p>
                    </IconAlert>
                </>
            )}

            <hr className="my-3" />

            {/* Subtotal */}
            <div className="d-flex justify-content-between py-1">
                <span className="text-muted fs--1">Subtotal</span>
                <span className="fs--1">${totales.dSubtotal.toFixed(2)}</span>
            </div>

            {/* IVA */}
            <div className="d-flex justify-content-between py-1">
                <span className="text-muted fs--1">IVA (16%)</span>
                <span className="fs--1">${totales.dIVA.toFixed(2)}</span>
            </div>

            {/* Tipo de cambio */}
            {totales.dTipoCambio !== 1 && (
                <div className="d-flex justify-content-between py-1">
                <span className="text-muted fs--2">Tipo de cambio</span>
                <span className="text-muted fs--2">
                    ${totales.dTipoCambio} MXN
                </span>
            </div>
            )}

            <hr className="my-3" />

            {/* Total destacado */}
            <div className="text-center mb-3">
                <div className="fs--1 text-muted mb-1">Total a pagar</div>
                <div className="fs-2 fw-bold text-primary">
                    {totales.dTotal === 0
                        ? '----'
                        : `${totales.dTotal.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD'
                          })} ${totales.vMoneda}`}
                </div>
            </div>

            {/* Forma de pago */}
            <Form.Select
                className="mb-3"
                onChange={(e) => onCambiarFormaPago(e.target.value)}
                value={totales.idCatFormaPago}
            >
                <option value="">Selecciona forma de pago</option>
                {formasPago.map((fp, index) => (
                    <option key={index} value={fp.idCatFormaPago}>
                        {fp.vDescripcionFormaPago}
                    </option>
                ))}
            </Form.Select>

            {/* Botón confirmar */}
            {confirmandoOrden ? (
                <Button variant="secondary" disabled className="w-100">
                    <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        className="me-2"
                        aria-hidden="true"
                    />
                    Procesando...
                </Button>
            ) : (
                <>
                    <Button
                        variant="success"
                        className="w-100"
                        type="submit"
                        onClick={onConfirmar}
                    >
                        Confirmar &oacute;rden &#10003;
                    </Button>
                    {!bIsNuevaOrden && onCancelar && (
                        <Button
                            variant="outline-secondary"
                            className="w-100 mt-2"
                            onClick={onCancelar}
                        >
                            Cancelar Edici&oacute;n
                        </Button>
                    )}
                </>
            )}

            <p className="fs--2 text-muted text-center mt-2 mb-0">
                Al confirmar aceptas los{' '}
                <a href="">T&eacute;rminos &amp; Condiciones</a>
            </p>
        </div>
    );
};

PanelDetalleOrden.propTypes = {
    totales: PropTypes.shape({
        vMoneda: PropTypes.string.isRequired,
        dTotal: PropTypes.number.isRequired,
        dSubtotal: PropTypes.number.isRequired,
        dIVA: PropTypes.number.isRequired,
        dTipoCambio: PropTypes.number.isRequired,
        dFechaVenta: PropTypes.string.isRequired,
        idCatFormaPago: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]).isRequired,
        bIsDistribucion: PropTypes.bool
    }).isRequired,
    datosFacturacion: PropTypes.object,
    formasPago: PropTypes.arrayOf(PropTypes.shape({
        idCatFormaPago: PropTypes.number.isRequired,
        vDescripcionFormaPago: PropTypes.string.isRequired
    })).isRequired,
    fechaLimitePago: PropTypes.bool.isRequired,
    confirmandoOrden: PropTypes.bool.isRequired,
    onCambiarFormaPago: PropTypes.func.isRequired,
    onCambiarFechaVenta: PropTypes.func.isRequired,
    onCambiarFechaVencimiento: PropTypes.func.isRequired,
    onConfirmar: PropTypes.func.isRequired,
    bIsNuevaOrden: PropTypes.bool,
    onCancelar: PropTypes.func
};

export default PanelDetalleOrden;
